import React, { useEffect, useState } from "react";
import FAQ from "../components/FAQs";
import SEO from "../components/SEO";
import DemoSequence from "../components/DemoSequence";
import ArchitectureFigure from "../components/ArchitectureFigure";
import PilotTimeline from "../components/PilotTimeline";

import controlRoomFull from "./../images/control-room-2026.png";
import controlRoom480 from "./../images/control-room-2026-480.webp";
import controlRoom960 from "./../images/control-room-2026-960.webp";
import controlRoom1200 from "./../images/control-room-2026-1200.webp";
import controlRoomMobile540 from "./../images/control-room-2026-mobile-540.webp";
import controlRoomMobile720 from "./../images/control-room-2026-mobile-720.webp";
import auditLogsFull from "./../images/audit-logs-2026.png";
import auditLogs480 from "./../images/audit-logs-2026-480.webp";
import auditLogs960 from "./../images/audit-logs-2026-960.webp";
import auditLogs1200 from "./../images/audit-logs-2026-1200.webp";
import auditLogsMobile540 from "./../images/audit-logs-2026-mobile-540.webp";
import auditLogsMobile720 from "./../images/audit-logs-2026-mobile-720.webp";

import { DEMO_LINK } from "../components/demoBooking";

export default function Landing() {
  const [eaMsg, setEaMsg] = useState("");
  const [eaBusy, setEaBusy] = useState(false);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    // The page scrolls inside App.js's .routeScroll, not the window, so a
    // window listener never saw a scroll and the bar never showed.
    const scroller = document.querySelector(".routeScroll") || window;
    const top = () => (scroller === window ? window.scrollY : scroller.scrollTop);
    const onScroll = () => setShowSticky(top() > 420);
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  const handleEarlyAccess = async (e) => {
    e.preventDefault();
    const form = e.target;
    const email = new FormData(form).get("email");
    if (!email) return;

    try {
      setEaBusy(true);
      setEaMsg("");

      const r = await fetch("https://api.bossdrop.ai/early-access", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (r.ok) {
        setEaMsg("✅ Thanks. You’re on the list.");
        window.fbq?.("track", "Lead");
        form.reset();
      } else {
        setEaMsg("⚠️ Couldn’t submit. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setEaMsg("⚠️ Couldn’t submit. Please try again.");
    } finally {
      setEaBusy(false);
    }
  };

  return (
    <main className="bd-page" id="top">
      <SEO
        title="BossDrop | AI Coordination Layer for Enterprise Ops"
        description="Coordination now runs on AI. BossDrop reads Slack, Teams, email, Jira, Asana, ClickUp, Notion and monday.com, then chases status and writes the exec brief."
        canonical="https://bossdrop.ai/"
        url="https://bossdrop.ai/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "BossDrop",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://bossdrop.ai/",
          description:
            "BossDrop is an AI coordination layer. It reads work signal across chat, email, tasks, and calendars, then chases status, escalates blockers, drafts tasks, and sends the executive brief. Approvals where they matter, every action audited.",
          publisher: {
            "@type": "Organization",
            name: "BossDrop",
            url: "https://bossdrop.ai/",
          },
        }}
      />

      {showSticky && (
        <div className="bd-sticky-cta" role="region" aria-label="Quick actions">
          <div className="bd-sticky-inner">
            <span className="bd-sticky-copy">
              Coordination now runs on AI.
            </span>
            <div className="bd-sticky-actions">
              <a className="bd-button" {...DEMO_LINK}>
                Book a demo
              </a>
            </div>
          </div>
        </div>
      )}

      <section className="bd-hero bd-hero-animated bd-hero-v3">
        <div className="bd-hero-inner">
          <p className="bd-pill reveal" data-reveal>
            ✅ 45-day pilot • automated from day 15 • every action logged
          </p>

          <h1 className="bd-title reveal" data-reveal style={{ "--d": "60ms" }}>
            Status chasing isn't a job.
            <br />
            <span className="fx-ai">
              <span className="fx-ai-text">Coordination now runs on AI.</span>
            </span>
          </h1>

          <p className="bd-sub reveal" data-reveal style={{ "--d": "140ms" }}>
            Chasing the update. Escalating the blocker. Writing the Friday brief.
            <br />
            <strong>Nobody should have to do it. Now nobody has to.</strong>
          </p>

          {/* One action in the hero (Oct 28 2026): the demo booking, loud and
              alone. Two buttons side by side, with an email field under them,
              read as a fork in the road, and a dropped email looked like a
              sign-up. The email list is at the bottom (#updates) for people
              who are not ready to talk. */}
          <div
            className="bd-hero-cta reveal"
            data-reveal
            style={{ "--d": "220ms" }}
          >
            <a className="bd-button bd-button-hero" {...DEMO_LINK}>
              Book a demo
              <span className="bd-arrow" aria-hidden="true">
                →
              </span>
            </a>
            {/* A quiet second way in that costs no button: the product demo
                below runs on a loop of about 30 seconds (DemoSequence). */}
            <p className="bd-hero-cta-note">
              20 minutes with the founder, or{" "}
              <a className="bd-hero-watch" href="#bossdrop-story">
                watch it run for 30 seconds
              </a>
              .
            </p>
          </div>

          <HeroSignalRow />
        </div>
      </section>

      {/* The architecture and the stack, one section (Nov 2 2026): the old
          coordination-layer drawing and "It runs inside the stack" merged into
          one live figure (ArchitectureFigure). #how-it-works stays here for
          older links. */}
      <section
        className="bd-demo archSection"
        id="how-it-works"
        aria-labelledby="how-it-works-title"
      >
        <div className="container">
          <header
            className="sectionHead reveal"
            data-reveal
            style={{ marginBottom: 22 }}
          >
            <span className="pill" style={{ marginBottom: 10 }}>
              The coordination layer
            </span>
            <h2 className="h2" id="how-it-works-title">
              One AI layer, inside the stack you already pay for
            </h2>
            <p className="sub">
              No migration, no new place to check. BossDrop reads the work in
              the tools your team already uses, decides what needs to happen,
              acts on it and logs every step.
            </p>
          </header>

          {/* The reveal stays on this wrapper: it sets its class on the
              element directly, and the figure's own root re-renders. */}
          <div className="reveal" data-reveal>
            <ArchitectureFigure />
          </div>
        </div>
      </section>

      {/* Proof, one section: one slipped task followed from the signal to
          Friday's brief (DemoSequence). Oct 28 2026: the separate "receipts"
          block (the brief and escalation screenshots) went; the demo shows
          the same sequence, and its email is the real brief in miniature.
          #sample-output stays on the demo for older links. */}
      <section className="bd-hub" id="bossdrop-story">
        <div className="container">
          <header
            className="sectionHead reveal"
            data-reveal
            style={{ marginBottom: 16 }}
          >
            <h2 className="h2">Watch BossDrop follow through</h2>
            <p className="sub">
              One slipped task, start to finish. It spots the slip, asks the
              owner, acts on the answer, and Friday's brief shows where it
              landed.
            </p>
          </header>

          <div className="storyboardGrid reveal" data-reveal id="sample-output">
            <div className="storyboardMain">
              <DemoSequence />
            </div>

            <div className="storyboardSide">
              <MiniStoryCard
                title="It acts, it doesn't summarize"
                text="Chases the update, escalates the blocker, drafts the task, books the decision meeting. Summaries are a byproduct, not the product."
              />
              <MiniStoryCard
                title="Automation you can supervise"
                text="Confidence thresholds route uncertain actions to a human approval queue. A guard layer blocks duplicate and cross-channel pings."
              />
              <MiniStoryCard
                title="Every action is on the record"
                text="An exportable audit log of what it did and why. That is the difference between an agent you demo and an agent you deploy."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Control, one section (Oct 25 2026): the Control Room and Audit Logs
          screenshots and the security block, which used to sit apart (the
          screenshots after the numbers, security after the pilot). #security
          stays on the security block for older links. */}
      <section className="bd-hub" id="control">
        <div className="container">
          <header
            className="sectionHead reveal"
            data-reveal
            style={{ marginBottom: 16 }}
          >
            <h2 className="h2">You stay in control</h2>
            <p className="sub">
              Set the scope and the rules before BossDrop acts, approve what
              matters from the queue, and see every action it took.
            </p>
          </header>

          <div className="hubMediaGrid reveal" data-reveal>
            <MediaCard
              title="Control Room"
              body="Set scope, permissions, ownership, and automation rules before BossDrop starts acting."
              fullImg={controlRoomFull}
              img={controlRoom960}
              srcSet={`${controlRoom480} 480w, ${controlRoom960} 960w, ${controlRoom1200} 1200w`}
              mobileSrcSet={`${controlRoomMobile540} 540w, ${controlRoomMobile720} 720w`}
              width={1200}
              height={750}
              mobileWidth={720}
              mobileHeight={1400}
              alt="BossDrop Control Room for the Northwind workspace, with all changes saved and Automation set to Automated. Daily status pings run automatically in Microsoft Teams. Auto-escalations (sent by email), task creates and updates in Asana, and meeting scheduling each require approval. The Team Directory shows Taylor Mendes and Priya Raman reporting to Dana Whitfield, who reports to Sam Okafor, the owner who approves actions. The executive report goes out weekly by email, Fridays at 7:00 AM Central time."
              tags={["Scope guardrails", "Ownership mapping", "Schedules"]}
            />
            <MediaCard
              title="Audit Logs"
              body="Every action BossDrop takes is visible, reviewable, and traceable across your operating stack."
              fullImg={auditLogsFull}
              img={auditLogs960}
              srcSet={`${auditLogs480} 480w, ${auditLogs960} 960w, ${auditLogs1200} 1200w`}
              mobileSrcSet={`${auditLogsMobile540} 540w, ${auditLogsMobile720} 720w`}
              width={1200}
              height={750}
              mobileWidth={720}
              mobileHeight={1490}
              alt="BossDrop Audit Logs page for the Northwind workspace, with search, outcome and type filters and an Export CSV button. Each row shows a time, a type badge, a summary and an outcome. Tuesday at 9:40 AM it asked Taylor Mendes in Teams about the vendor SSO keys and followed up Wednesday at 9:45 AM, skipping a duplicate email ping. Thursday at 9:21 AM it emailed an escalation to Dana Whitfield, Taylor's manager, and at 9:24 AM it added an escalation note to the Asana task; Sam Okafor approved both. A drafted Asana task for Priya Raman is queued for approval with Approve and Deny buttons. Friday at 7:00 AM it sent the Weekly Executive Summary."
              tags={["Timestamps", "Traceability", "Exportable logs"]}
            />
          </div>

          <div
            className="bd-security--enterprise controlSecurity reveal"
            data-reveal
            id="security"
          >
            <div className="sec-head">
              <h3 className="controlSubhead">Enterprise-grade security</h3>
              <p className="sub">
                Least-privilege access, audit logs, encryption in transit and at
                rest, and clear scope control from day one.
              </p>
              <div className="ctaRow">
                <a className="btn btnPrimary" href="/contact-sales">
                  Talk to sales
                </a>
                <a className="btn btnGhost" {...DEMO_LINK}>
                  Book a demo
                </a>
              </div>
            </div>

            <ul className="bullets">
              <li>SSO-ready and built for per-tenant isolation</li>
              <li>Fine-grained permissions and exportable audit trail</li>
              <li>Configurable retention and scope controls</li>
              <li>Your data is never used to train shared models</li>
            </ul>

            <div className="sec-badges">
              <div className="sec-badge">
                <GDPRBadge />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What the coordination work is worth (the coordinator roles you
          don't hire), after the product and its controls, handing off to the
          pilot steps (#pilot). The capability grid lives on /features. */}
      <TheNumbers />

      <section className="bd-how" id="pilot">
        <div className="container">
          <h2 className="h2 reveal" data-reveal>
            How the 45-day pilot&nbsp;goes
          </h2>
          <p className="sub pilotLead reveal" data-reveal>
            Start with one team. Two weeks of watching set the baseline, then
            BossDrop runs on its own. Measure blocker age and brief turnaround
            against that baseline, and decide from there.
          </p>

          <PilotTimeline
            start={{
              when: "Day 1",
              title: "Connect and scope",
              text: "Slack or Teams, your task tool, and calendar, with minimal scopes. One team and one executive sponsor. Agree on two or three KPIs, and in-scope teams get told what it is and what it can see.",
            }}
            watch={{
              when: "Days 1 to 14",
              title: "Watch and set the baseline",
              text: "BossDrop reads everything and sends the weekly risk report, but sends no nudges and no escalations. That gives a clean baseline, much of it pulled from your task tool's own history.",
            }}
            run={{
              when: "Days 15 to 45",
              title: "It runs on its own",
              text: "It chases status, follows up on decisions and escalates what stays stuck, all on its own, and every action lands in the audit log. Any automation can go back to approval at any time.",
            }}
            weekly={{
              when: "Every week",
              title: "The weekly report",
              text: "KPIs, risks and approvals in one skimmable report, starting in week one. Each dot on the line is one.",
            }}
            end={{
              when: "Day 45",
              title: "Readout and decide",
              text: "Your KPIs against the two-week baseline, what BossDrop learned about how your team runs, and the audit log. Then you decide: expand, narrow the scope, or stop. What it learned is yours to export either way.",
            }}
          />

          <div
            className="ctaRow centerRow reveal"
            data-reveal
            style={{ "--d": "300ms" }}
          >
            <a className="btn btnPrimary" {...DEMO_LINK}>
              Book a pilot call
            </a>
            <a className="btn btnGhost" href="#pilot-faq">
              Read FAQ
            </a>
          </div>
        </div>
      </section>

      <section id="pilot-faq">
        <FAQ
          items={[
            {
              q: "What is BossDrop, exactly?",
              a: "BossDrop is an AI coordination layer. It reads work signal across Slack, Microsoft Teams, email, Asana, Jira, Linear, ClickUp, Notion, monday.com, Microsoft Planner, GitHub, Google and Outlook calendars, HubSpot, and Pipedrive, then takes real actions from a fixed vocabulary: chasing status, escalating blockers, drafting and assigning tasks, proposing decision meetings, replying to email threads, and sending the executive brief. People can also just reply to ai@bossdrop.ai and it answers like a manager would, then folds that thread into its context.",
            },
            {
              q: "Are you telling me to fire my managers?",
              a: "No. BossDrop makes no staffing recommendations and does not rank people. What it does is take the relay-and-chase work off the organization entirely, so the coordination work stops growing every time the company does. The point is to put that time back into judgment work, like hiring, prioritization, and customers, and to stop adding coordination capacity as you scale.",
            },
            {
              q: "Why isn't this just a feature of Slack, Copilot, or Asana?",
              a: "Because coordination fails in the seams between tools, and a tool can only see its own data. Slack cannot chase the Jira ticket, Jira cannot read the email thread, and neither one knows the calendar is full. BossDrop sits above all of them. Your existing tools stay exactly where they are, and nothing gets replaced or migrated.",
            },
            {
              q: "Who is accountable when software does the coordinating?",
              a: "You are, and the product is built for that. Confidence thresholds hold back actions BossDrop is unsure about, any automation can be set to wait for human approval, a guard layer blocks duplicate and cross-channel actions (it will not email someone about something it already pinged them about in Teams), and every action BossDrop takes lands in a full, exportable audit log. Access is least-privilege and scoped per workspace, with per-tenant isolation, configurable retention, GDPR alignment, and SSO available depending on your setup. Your data is never used to train shared models.",
            },
            {
              q: "Will my team feel surveilled?",
              a: "BossDrop is built to keep work moving, and it never ranks people or makes staffing recommendations. It does learn how work moves, and some of that is about one person: how quickly they usually answer, when they are usually around, how often their work lands on its due date, which blockers they clear, which handoffs slip. It uses that to check in at a sensible time and to give someone the right amount of time before going to their manager. Only that person and workspace admins can see those lines, they hold timing and never the text of a message, and an admin can correct or remove any of them. You set exactly which channels, boards, and projects are in scope from the Control Room, in-scope teams are notified, the guard layer caps how often anyone gets pinged, and every action taken is visible to admins in the audit log. Its reports flag structural drift (stale tasks, missing owners, slipped dates), not people.",
            },
            {
              q: "What does rollout actually look like?",
              a: "Connect your tools, set scope, ownership, and automation rules in the Control Room, and agree on two or three KPIs. For the first two weeks BossDrop only watches: it reads everything and sends the weekly risk report, but no nudges or escalations, so you get a clean baseline. From day 15 it switches to automated and runs on its own with one team, and you can send any automation back to approval from the Control Room. Measure the work, not the people: blocker age, cycle time, how long a status roll-up takes to produce, and the count of actions in the audit log. At day 45 you compare against the baseline and decide. If the coordination work did not move, you walk away and your tools are exactly where you left them.",
            },
            {
              q: "What happens to what BossDrop learned if we leave?",
              a: "You take it with you. What BossDrop learns about how your team runs is one structured document: owners, cadence, handoffs, policies, and thresholds. Your admins can export all of it as plain text or JSON at any time from the Memory page, without asking us. What stays behind is the loop that keeps it true: watching real work, rewriting that document every day, and changing what BossDrop does tomorrow because of it.",
            },
          ]}
          reveal={false}
        />
      </section>

      {/* For someone who is not ready to talk: the email list, moved out of
          the hero so the hero has one action. Quieter than the demo button on
          purpose. */}
      <section
        className="container bd-later reveal"
        data-reveal
        id="updates"
        aria-labelledby="updates-title"
      >
        <h2 className="h2" id="updates-title">
          Not ready for a call?
        </h2>
        <p className="sub">Leave your work email and we’ll keep you posted.</p>
        <form className="bd-later-form" onSubmit={handleEarlyAccess}>
          <input
            type="email"
            name="email"
            placeholder="you@company.com"
            aria-label="Work email"
            required
          />
          <button type="submit" className="btn btnGhost" disabled={eaBusy}>
            {eaBusy ? "Sending…" : "Keep me posted"}
          </button>
        </form>
        {eaMsg && (
          <p className="formStatus" role="status">
            {eaMsg}
          </p>
        )}
        <p className="muted bd-later-fine">
          By entering your email, you agree to receive product updates and
          marketing emails. Unsubscribe anytime.
        </p>
      </section>
    </main>
  );
}

function HeroSignalRow() {
  return (
    <div
      className="bd-marquee reveal"
      data-reveal
      style={{ "--d": "320ms", width: "56%", minWidth: 320 }}
    >
      <div className="bd-marquee-track">
        <span>Chases the status update</span>
        <span>Escalates the blocker</span>
        <span>Drafts and assigns the task</span>
        <span>Proposes the decision meeting</span>
        <span>Writes the Friday exec brief</span>
        <span>Answers the email thread</span>
        <span>Approvals where they matter</span>
        <span>Every action logged and exportable</span>

        <span aria-hidden="true">Chases the status update</span>
        <span aria-hidden="true">Escalates the blocker</span>
        <span aria-hidden="true">Drafts and assigns the task</span>
        <span aria-hidden="true">Proposes the decision meeting</span>
        <span aria-hidden="true">Writes the Friday exec brief</span>
        <span aria-hidden="true">Answers the email thread</span>
        <span aria-hidden="true">Approvals where they matter</span>
        <span aria-hidden="true">Every action logged and exportable</span>
      </div>
    </div>
  );
}

function MiniStoryCard({ icon, title, text }) {
  return (
    <article className="miniStoryCard">
      {icon && <div className="miniStoryIcon">{icon}</div>}
      <h3>{title}</h3>
      <p>{text}</p>
    </article>
  );
}

function MediaCard({
  title,
  body,
  fullImg,
  img,
  srcSet,
  mobileSrcSet,
  mobileWidth,
  mobileHeight,
  alt,
  width,
  height,
  tags = [],
}) {
  return (
    <article className="mediaCard">
      <div className="mediaCardTop">
        <h3>{title}</h3>
        <p>{body}</p>
      </div>

      <div className="mediaCardFrame">
        {mobileSrcSet ? (
          // A phone crop under 560px, and the
          // image links to the full-size PNG (opens in a new tab).
          <a
            className="outputDeckZoom"
            href={fullImg}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open full-size image"
          >
            <picture>
              <source
                media="(max-width:560px)"
                srcSet={mobileSrcSet}
                sizes="calc(100vw - 116px)"
                width={mobileWidth}
                height={mobileHeight}
              />
              <img
                src={img}
                srcSet={srcSet}
                sizes="(max-width: 980px) calc(100vw - 150px), 461px"
                alt={alt}
                width={width}
                height={height}
                loading="lazy"
                decoding="async"
              />
            </picture>
          </a>
        ) : (
          <img src={img} alt={alt} loading="lazy" decoding="async" />
        )}
      </div>

      <div className="mediaTags">
        {tags.map((tag, i) => (
          <span key={i}>{tag}</span>
        ))}
      </div>
    </article>
  );
}

// #the-numbers, part 1: published research. Each line follows the
// landing-page-safe wording from the Oct 2026 stats review, so it never says
// more than the source does. Keep figure, line and source together.
const NUMBERS_RESEARCH = [
  {
    figure: "58%",
    line: "of their workday goes to “work about work” (status updates, searching for information, switching apps), knowledge workers say.",
    source: "Asana Anatomy of Work Index, 2023",
    url: "https://investors.asana.com/news-releases/news-release-details/asana-anatomy-work-global-index-2023-smart-collaboration-and",
  },
  {
    figure: "28%",
    line: "of their time goes to bureaucratic chores, people report. Two-thirds say bureaucracy slows decisions.",
    source: "Hamel & Zanini survey of 7,000+ HBR readers, 2017",
    url: "https://hbr.org/2017/08/what-we-learned-about-bureaucracy-from-7000-hbr-readers",
  },
  {
    figure: "25%",
    line: "of the workweek is spent searching for information.",
    source: "Atlassian State of Teams, 2025",
    url: "https://www.atlassian.com/blog/state-of-teams-2025",
  },
  {
    // PMI publishes no single on-time figure, so the blend gets a hedge
    qualifier: "About",
    figure: "4 in 10",
    line: "projects miss their original schedule, project leaders estimate.",
    source: "PMI Pulse of the Profession, 2025 (about 6 in 10 finish on schedule)",
    url: "https://www.pmi.org/-/media/pmi/documents/public/pdf/learning/thought-leadership/pulse/pulse_of_the_profession_2025-1.pdf",
  },
];

// #the-numbers, part 2: the central case of
// All BossDrop/docs/research/coordination_model.py (60 people, $13.25M
// loaded). `width` is each step's share of the $2.78M drag pool, so the bars
// are drawn to scale. Re-run the model before changing any figure here.
const NUMBERS_FLOW = [
  {
    value: "$2.78M",
    label: "Lost to organizational drag",
    cut: "21% of capacity (Bain)",
    width: "100%",
    tone: "isLost",
  },
  {
    value: "$974K",
    label: "Mechanical relay: status, roll-ups, routing, pings",
    cut: "Amdahl ceiling: 35% of drag",
    width: "35%",
    tone: "",
  },
  {
    value: "$390K",
    label: "The part BossDrop does",
    cut: "Tool efficacy: 40%",
    width: "14%",
    tone: "",
  },
  {
    value: "$331K",
    label: "After the review work it creates",
    cut: "New review work: minus 15%",
    width: "11.9%",
    tone: "",
  },
  {
    value: "$83K",
    label: "Turns into real output",
    cut: "Conversion to output: 25%",
    width: "2.98%",
    tone: "isOk",
  },
];

/* Research strip, then one modeled company with and without BossDrop.
   Every figure is final in the markup: the only motion is the reveal and the
   bars growing in, and both switch off under prefers-reduced-motion. */
function TheNumbers() {
  return (
    <section className="bd-demo bdNum" id="the-numbers">
      <div className="container">
        <header
          className="sectionHead reveal"
          data-reveal
          style={{ marginBottom: 28 }}
        >
          <span className="pill" style={{ marginBottom: 10 }}>
            The cost of coordination
          </span>
          <h2 className="h2">The coordinator you don't hire</h2>
          <p className="sub">
            Most of what coordination costs never gets its own line in the
            budget. Published research puts a size on it. Then we modeled one
            company to see what changes with BossDrop. Every figure links to
            its source or states its assumption.
          </p>
        </header>

        <p className="bdNumEyebrow reveal" data-reveal>
          The research
        </p>
        <ul className="bdNumStrip">
          {NUMBERS_RESEARCH.map((s, i) => (
            <li
              key={s.figure}
              className="bdNumStat reveal"
              data-reveal
              style={{ "--delay": `${i * 80}ms` }}
            >
              <strong className="bdNumFigure">
                {s.qualifier && (
                  <span className="bdNumFigureQual">{s.qualifier} </span>
                )}
                {s.figure}
              </strong>
              <p>{s.line}</p>
              <a
                className="bdNumSource"
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {s.source}
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </li>
          ))}
        </ul>

        <p className="bdNumEyebrow reveal" data-reveal>
          The model
        </p>
        <h3 className="bdNumLead reveal" data-reveal>
          We modeled a 60-person company as it grows: once without
          BossDrop, once with it.
        </h3>

        <div className="bdNumCompare">
          <article className="bdNumSide bdNumSideWithout reveal" data-reveal>
            <span className="bdNumTag">Without BossDrop</span>
            <p className="bdNumBig">
              <span>About</span>
              <strong>$2.8M</strong>
              <span>a year</span>
            </p>
            <p className="bdNumBigLabel">
              of capacity lost to organizational drag. Bain's average is more
              than 20% of productive capacity; the model uses 21%.
            </p>
            <ul className="bdNumList">
              <li>
                <strong>$395K a year</strong> planned for two coordination
                hires: a technical program manager and a project manager, at
                loaded cost.
              </li>
            </ul>
            <p className="bdNumPunch">
              <strong>Status roll-ups</strong> assembled by hand.
            </p>
          </article>

          <article
            className="bdNumSide bdNumSideWith reveal"
            data-reveal
            style={{ "--delay": "120ms" }}
          >
            <span className="bdNumTag">With BossDrop</span>
            <p className="bdNumBig">
              <span>About</span>
              <strong>$478K</strong>
              <span>a year in value</span>
            </p>
            <p className="bdNumBigLabel">
              Range: $403K to $769K across pessimistic and optimistic
              assumptions. About 3.6% of total people cost. Gross, before
              BossDrop's cost.
            </p>

            {/* $395,200 of $477,994: the legend below carries the values */}
            <div className="bdNumSplit" aria-hidden="true">
              <span className="bdNumSplitRoles" style={{ "--w": "82.7%" }} />
              <span className="bdNumSplitTime" style={{ "--w": "17.3%" }} />
            </div>
            <ul className="bdNumList bdNumLegend">
              <li className="isRoles">
                <strong>$395K</strong> is the two coordination roles you don't
                need to hire.
              </li>
              <li className="isTime">
                <strong>$83K</strong> is time returned to the team as real
                output.
              </li>
            </ul>
            <p className="bdNumPunch">
              <strong>83%</strong> of the value comes from roles you don't
              have to hire, not from working faster.
            </p>
          </article>
        </div>

        <figure className="bdNumFlow reveal" data-reveal>
          <figcaption className="bdNumFlowHead">
            <h3>How $2.78M of drag becomes $83K of output</h3>
            <p>
              These are the discounts we apply to our own numbers. Each step
              keeps only part of the one above it, and the bars are drawn to
              scale.
            </p>
          </figcaption>
          <ol className="bdNumBars">
            {NUMBERS_FLOW.map((row, i) => (
              <li
                key={row.value}
                className={`bdNumBar ${row.tone}`.trim()}
                style={{ "--w": row.width, "--i": i }}
              >
                <div className="bdNumBarText">
                  <span className="bdNumBarLabel">{row.label}</span>
                  <span className="bdNumBarCut">{row.cut}</span>
                </div>
                <div className="bdNumBarTrack" aria-hidden="true">
                  <span className="bdNumBarFill" />
                </div>
                <strong className="bdNumBarValue">{row.value}</strong>
              </li>
            ))}
          </ol>
          <p className="bdNumSum">
            $83K of output, plus $395K of coordination roles you don't hire:{" "}
            <strong>about $478K a year.</strong>
          </p>
        </figure>

        <div className="bdNumNote reveal" data-reveal role="note">
          <p className="bdNumNoteHead">Modeled, not measured</p>
          <p>
            Inputs are published research plus stated assumptions: a loaded
            people cost of $13.25M for 60 people, two coordination salaries
            set high on purpose, 35% of drag is mechanical relay, 40%
            efficacy, 15% new review work, and 25% of freed time becomes
            output. Your pilot measures your real numbers: blocker age, cycle
            time and report turnaround.
          </p>

          <details className="bdNumHow">
            <summary>How we modeled this</summary>
            <ul>
              <li>
                <strong>The company.</strong> 60 people at 37.5 productive
                hours a week, with a loaded people cost of $13.25M a year.
              </li>
              <li>
                <strong>Organizational drag: 21% of capacity.</strong>{" "}
                <a
                  href="https://www.bain.com/insights/great-companies-obsess-over-productivity-hbr/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Bain &amp; Company
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>{" "}
                estimates the average large company loses more than 20% of its
                productive capacity to drag. The best-run quarter of companies
                lose about 13%, against about 24% for the rest. The low and
                high cases use those two figures.
              </li>
              <li>
                <strong>Mechanical relay: 35% of drag.</strong> Status
                updates, roll-ups, routing and pings. This is the ceiling on
                what any coordination tool can touch (the Amdahl ceiling).
                Assumption.
              </li>
              <li>
                <strong>Tool efficacy: 40%.</strong> The share of that relay
                work BossDrop actually does. Assumption.
              </li>
              <li>
                <strong>New review work: 15%.</strong> Approvals and checks
                take back part of what it frees. Assumption, tested from 5% to
                30%.
              </li>
              <li>
                <strong>Conversion to output: 25%.</strong> The share of
                freed time that turns into real output. The least measured
                input in the model. Assumption.
              </li>
              <li>
                <strong>Roles not hired: $395,200 a year.</strong> One
                technical program manager ($178K base) and one project
                manager ($126K base), loaded at 1.3 times base. Base pay is
                set at the 75th to 90th percentile for a non-Bay-Area US metro
                (Built In Austin averages, scaled up with Levels.fyi pay
                ratios), so it runs high on purpose. The 1.3 times loading
                follows{" "}
                <a
                  href="https://www.bls.gov/news.release/ecec.nr0.htm"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  BLS Employer Costs for Employee Compensation
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
                . That a company this size would otherwise hire both is an
                assumption.
              </li>
              <li>
                <strong>Range.</strong> The pessimistic case (13% drag, 25%
                relay, 25% efficacy, 30% review work, 10% conversion) gives
                $403K a year. The optimistic case (24%, 45%, 55%, 5%, 50%)
                gives $769K.
              </li>
              <li>
                <strong>Gross, not net.</strong> The figures do not subtract
                what BossDrop costs. The four research figures above come from
                surveys, so they are self-reported.
              </li>
            </ul>
          </details>
        </div>

        <div className="ctaRow centerRow bdNumCta reveal" data-reveal>
          <a className="btn btnPrimary" {...DEMO_LINK}>
            Book a demo
          </a>
          <a className="btn btnGhost" href="#pilot">
            See how the pilot measures it
          </a>
        </div>
      </div>
    </section>
  );
}

function GDPRBadge(props) {
  return (
    <svg
      viewBox="0 0 120 120"
      width="40"
      height="40"
      aria-label="GDPR compliant"
      role="img"
      {...props}
    >
      <defs>
        <path
          id="gdpr-star"
          d="M0,-7 1.9,-2.2 6.5,-2.2 2.6,0.8 4,5.5 0,3 -4,5.5 -2.6,0.8 -6.5,-2.2 -1.9,-2.2 Z"
        />
      </defs>
      <circle cx="60" cy="60" r="56" fill="#0A5BE1" />
      <g fill="#FFDA44" transform="translate(60 60)">
        {[...Array(12)].map((_, i) => (
          <g key={i} transform={`rotate(${i * 30}) translate(0,-40)`}>
            <use href="#gdpr-star" />
          </g>
        ))}
      </g>
      <text
        x="60"
        y="73"
        textAnchor="middle"
        fontFamily="Inter, system-ui, sans-serif"
        fontWeight="800"
        fontSize="26"
        fill="#fff"
      >
        GDPR
      </text>
    </svg>
  );
}
